import React from 'react'
import { getBrowserFromUserAgent, noop } from '@sketch/utils'
import { StorageItemFragment } from '@sketch/gql-types'

// Type similar to the src/modules/storageItems/types.ts StorageItemIdentity
type StorageItemIdentity = Pick<
  StorageItemFragment,
  'identifier' | '__typename'
>

export type DragPayload = {
  item: StorageItemIdentity
  relatedItems: StorageItemIdentity[]
}

interface UseDragProps<T> {
  item: T
  relatedItems: T[]
  customDragImage?: boolean
  customDragImageStyles?: React.CSSProperties
  createCustomElement?: (element: HTMLElement) => HTMLElement
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void
  onDragEnd?: (event: React.DragEvent<HTMLElement>) => void
}

function composeHandlers<E>(
  ...fns: Array<((event: E) => void) | undefined>
): (event: E) => void {
  return (event: E) => {
    for (const fn of fns) {
      fn?.(event)
    }
  }
}

function applyStyles(styles: React.CSSProperties, element: HTMLElement): void {
  for (const [property, value] of Object.entries(styles)) {
    if (value) {
      // Transform camelCase properties to kebab-case for CSS
      const cssProperty = property.replace(
        /[A-Z]/g,
        match => `-${match.toLowerCase()}`
      )
      element.style.setProperty(cssProperty, value.toString())
    }
  }
}

const identityFunction = <T>(e: T) => e

/**
 * This function exposes handlers to set a custom drag image for an element.
 *
 * Drag and Drop API already allow to set a custom drag image but with several
 * limitations (when using `event.dataTransfer.setDragImage` with a cloned
 * element, the browser generates a ghost image, but you can't tweak
 * almost any visual aspect of it).
 *
 * So, the only option at this point is to create a "real" custom drag element,
 * hiding the default native ghost image. This gives complete freedom to style
 * the drag image, but it adds complexity and is error-prone.
 */
const customDragImageHandlers = (
  styles: UseDragProps<{}>['customDragImageStyles'],
  createCustomElement: UseDragProps<{}>['createCustomElement'] = identityFunction
) => {
  const onCustomDragStart = (event: React.DragEvent<HTMLElement>) => {
    const element = createCustomElement(event.target as HTMLElement)

    // Hide browser native drag image using a custom one
    const hiddenDragImage = element.cloneNode(true) as HTMLElement
    hiddenDragImage.id = 'hidden-drag-image'
    hiddenDragImage.style.opacity = '0'
    document.body.appendChild(hiddenDragImage)

    event.dataTransfer.setDragImage(hiddenDragImage, 0, 0)

    // Set custom drag image
    const customDragImage = element.cloneNode(true) as HTMLElement
    customDragImage.id = 'custom-drag-image'
    customDragImage.style.position = 'absolute'
    customDragImage.style.zIndex = '99999'
    customDragImage.style.pointerEvents = 'none'
    customDragImage.style.opacity = '0'
    customDragImage.style.width = `${element.clientWidth}px`

    if (styles) {
      applyStyles(styles, customDragImage)
    }

    // Let's make sure we remove any existing custom drag image before
    // appending a new one
    document.getElementById('custom-drag-image')?.remove()

    document.body.appendChild(customDragImage)
    document.body.style.overflow = 'hidden'
  }

  const onCustomDragEnd = () => {
    document.querySelectorAll('#hidden-drag-image').forEach(hiddenDragImage => {
      hiddenDragImage.remove()
    })

    document.querySelectorAll('#custom-drag-image').forEach(hiddenDragImage => {
      hiddenDragImage.remove()
    })

    document.body.style.overflow = 'auto'
  }

  const onCustomDrag = (event: React.DragEvent<HTMLElement>) => {
    const customDragImage = document.getElementById('custom-drag-image')

    if (customDragImage) {
      // In some browsers the event.clientX and event.clientY returns 0 before
      // the drag starts, we want to avoid this situation
      if (event.clientX > 0 && event.clientY > 0) {
        // By default we set the opacity to 0 so it doesn't show up, but we
        // want to show the custom drag image when the drag event starts
        if (customDragImage.style.opacity === '0') {
          customDragImage.style.opacity = styles?.opacity
            ? `${styles?.opacity}`
            : ''
        }
        const x = event.clientX - customDragImage.clientWidth / 2
        const y = event.clientY - customDragImage.clientHeight / 2
        const translation = `translate(${x}px, ${y}px)`

        customDragImage.style.transform = styles?.transform
          ? `${translation} ${styles?.transform}`
          : translation
      }
    }
  }

  return {
    onCustomDragStart,
    onCustomDragEnd,
    onCustomDrag,
  }
}

type EntityIdentify = {
  __typename: string
  identifier: string
}

/**
 * Hook that exposes attributes to drag an element, accepts dragging both
 * shares or projects (includes the shareId or projectId in the event.dataTransfer data)
 */
export const useDrag = <T extends EntityIdentify>({
  item,
  relatedItems,
  customDragImage,
  customDragImageStyles,
  createCustomElement,
  onDragStart: externalOnDragStart,
  onDragEnd: externalOnDragEnd,
}: UseDragProps<T>) => {
  const { onCustomDragStart, onCustomDragEnd, onCustomDrag } =
    customDragImageHandlers(customDragImageStyles, createCustomElement)

  const onDragStart = (event: React.DragEvent<HTMLElement>) => {
    event.stopPropagation()

    const itemIsOnSelection = relatedItems.find(
      relatedItem => relatedItem.identifier === item.identifier
    )

    /** Set data-being-dragged to true so we know which element started the drag */
    /** This value is later reset in onDragEnd (down below) */
    const element = event.target as HTMLElement

    /**
     * To avoid issues when dragging an item that is not part of the current selection,
     * we ensure that the related items always include the dragged item itself.
     */
    const safeguardedRelatedItems = itemIsOnSelection ? relatedItems : [item]

    const stringifiedItem = JSON.stringify(item)
    const stringifiedRelatedItems = JSON.stringify(safeguardedRelatedItems)

    const dataTransferPayload = JSON.stringify({
      item,
      relatedItems: safeguardedRelatedItems,
    })

    event.dataTransfer.setData('text/plain', dataTransferPayload)
    event.dataTransfer.effectAllowed = 'move'

    // When dragging over an element, dataTransfer.getData('text/plain') is not
    // available, so we store the dragged element info in a data attribute
    element.dataset.beingDragged = stringifiedItem
    element.dataset.draggedRelatedItems = stringifiedRelatedItems
  }

  const onDragEnd = (event: React.DragEvent<HTMLElement>) => {
    /** Reset the data-being-dragged attribute  */
    const element = event.target as HTMLElement
    delete element.dataset.beingDragged
    delete element.dataset.draggedRelatedItems
  }

  /**
   * Firefox has an old bug regarding drag events and screen coordinates,
   * so we can't use custom drag images with this browser.
   * See: https://bugzilla.mozilla.org/show_bug.cgi?id=505521
   */
  const applyCustomDragImage =
    getBrowserFromUserAgent(navigator.userAgent || '') !== 'Firefox' &&
    customDragImage

  return {
    onDragStart: composeHandlers(
      ...[
        onDragStart,
        applyCustomDragImage ? onCustomDragStart : noop,
        externalOnDragStart,
      ]
    ),
    onDragEnd: composeHandlers(
      ...[
        onDragEnd,
        applyCustomDragImage ? onCustomDragEnd : noop,
        externalOnDragEnd,
      ]
    ),
    onDrag: applyCustomDragImage ? onCustomDrag : undefined,
  }
}
